import {Address} from '@app/models/address.model';
import { sortByKey } from '@app/utils';
import { HealthPlan } from '@app/models/health-plan.model';
import { FormControl, Validators } from '@angular/forms';

export interface Email {
  id: string;
  address: string;
  primary: boolean;
  confirmed: boolean;
  confirmed_date: Date;
}

export interface IPhone {
  id?: string;
  number: string;
  country: string;
  type: 1 | 2;
  primary: boolean;
  confirmed: boolean;
  confirmed_date: Date;
}

export class Employer {
  id: string;
  country: string;
  name: string;
}

export class Phone implements IPhone {
  id: string;
  number: string;
  country = 'US';
  type: 1 | 2 = 1;
  primary = false;
  confirmed = false;
  confirmed_date = null;

  constructor(phone: string | Partial<IPhone>) {
    if (typeof phone === 'string') {
      this.number = phone.replace('+1', '');
    } else {
      Object.assign(this, phone);
      this.number = phone.number.replace('+1', '');
    }
  }
}

export type BloodType = 'A+' | 'A-' | 'B+' | 'B-' | 'O+' | 'O-' | 'AB+' | 'AB-';

export class Profile {
  id: string;
  first_name: string;
  initial?: string;
  last_name: string;
  birth_date: Date;
  ssn: string;
  partial_ssn: string;
  national_identifier: string;
  ethnicity: number;
  gender: 1 | 2;
  abartys_id: string;
  dependents?: Dependent[] = [];
  tags: Employer[] = [];
  blood_type?: BloodType;
  emergency_info?: {
    name: string,
    phone: string
  };

  addresses: Address[] = [];
  emails: Email[] = [];
  phones: Phone[] = [];
  health_plans_cards: HealthPlan[] = [];
  all_health_plans_cards: HealthPlan[] = [];
  sms_notify:boolean;

  constructor(profile) {
    Object.assign(this, profile);
    // console.log("companys 2", {id:1, name: 'Banco X'}, {id:2, name: 'Clinic Z'});
    this.birth_date = new Date(
      typeof this.birth_date === 'string'
        ? this.birth_date + 'T00:00:00'
        : this.birth_date
    );

    this.health_plans_cards = this.health_plans_cards
      ?.map(card => ({ ...card, person: this.id })) || [];

    if (this instanceof Dependent) {
      // Don't apply anything after here to Dependents. Only the main Profile
      return;
    }

    this.addresses = [...this.addresses]
      ?.map(address => new Address(address));
    this.emails = [...this.emails]
      ?.sort(sortByKey('-primary')).map(email => email as Email);
    this.phones = [...this.phones]
      ?.sort(sortByKey('-primary')).map(phone => new Phone(phone));
    // {id:1, name: 'Banco X'}, {id:2, name: 'Clinic Z'}
    // console.log("companys", {id:1, name: 'Banco X'}, {id:2, name: 'Clinic Z'});


    this.emergency_info = this.emergency_info || { name: '', phone: '' };
    this.dependents = this.dependents?.map(dep => new Dependent(dep));

    this.all_health_plans_cards = [
      ...this.health_plans_cards,
      ...this.dependents
        ?.map(dependent => dependent.health_plans_cards).flat() || []
    ].sort(sortByKey('-rank'));
  }

  static validate(changes: Partial<Profile>): boolean {
    for (const change in changes) { // Iterate over changed profile fields
      if (!(change in profileValidators)) {
        continue; // If no validators are present, assume valid
      }

      // Multiple nested fields validation
      const field = changes[change];
      if (typeof field === 'object' && field.length) {
        for (const item in field) {
          const nested = field[item];

          for (const property in nested) {
            if (!(property in profileValidators[change])) {
              continue;
            }

            const control = new FormControl(
              nested[property],
              ...profileValidators[change][property]
            );

            if (!control.valid) {
              return false;
            }
          }
        }
      } else { // Single field validation
        const control = new FormControl(
          field,
          ...profileValidators[change] // Get validators using field names
        );

        if (!control.valid) {
          return false;
        }
      }
    }

    return true; // Consider valid if no validation was performed.
  }
}

export interface ProfileToken {
  exp: number;
  has_accepted_terms: boolean;
  abartys_id: string;
  has_completed_hra: boolean;
  databases: string[];
  id: string;
  user_id: number;
}

export class Dependent extends Profile {
  relationship?: 1 | 2 | 3 | 4;
  isDependent?: boolean;

  constructor(profile) {
    super(profile);
  }
}

// Use field names from Profile model to map Validators lookup.
export const profileValidators = {
  ssn: [Validators.pattern(/^\d{9}$/)],
  phones: {
    number: [Validators.pattern(/^\d{10}$/)]
  }
};
